import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { saveForm, getUserForms, getFormById, deleteForm, duplicateForm } from '../../services/formService';
import { generateId } from '../../utils/idGenerator';

// Field types
export type FieldType = 
  'text' | 
  'textarea' | 
  'number' | 
  'select' | 
  'checkbox' | 
  'radio' | 
  'date' | 
  'document' | 
  'paragraph' |
  'header';

// Form field model
export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  variableName: string;
  options?: { label: string; value: string }[];
  defaultValue?: any;
}

interface SimpleFormDesignerProps {
  onFormSave?: (formId: string) => void;
}

// Separate component for field property input to maintain focus
interface FieldPropertyInputProps {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  placeholder?: string;
  type?: string;
}

const FieldPropertyInput: React.FC<FieldPropertyInputProps> = React.memo(({ value, onChange, onFocus, placeholder, type = 'text' }) => {
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };
  
  return (
    <input 
      type={type}
      value={localValue} 
      onChange={handleChange}
      onFocus={onFocus}
      className="block w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      placeholder={placeholder}
    />
  );
});

// Field properties panel component - moved outside and memoized
interface FieldPropertiesProps {
  field: FormField;
  updateField: (id: string, updates: Partial<FormField>) => void;
  setSelectedFieldId: (id: string | null) => void;
  setIsEditingProperties: (value: boolean) => void;
  formName: string;
  fields: FormField[];
}

const FieldProperties: React.FC<FieldPropertiesProps> = React.memo(({ 
  field, 
  updateField, 
  setSelectedFieldId, 
  setIsEditingProperties,
  formName,
  fields 
}) => {
  const [variableError, setVariableError] = useState<string | null>(null);
  const [showAutoSuggestion, setShowAutoSuggestion] = useState(false);
  
  // Generate form-safe name
  const formSafeName = formName.toLowerCase().replace(/[^\w]/g, '') || 'form';
  
  // Validate variable name on change
  const handleVariableNameChange = (value: string) => {
    const validation = validateVariableName(value);
    
    if (!validation.isValid) {
      setVariableError(validation.error || null);
    } else {
      // Check for duplicates
      const isDuplicate = fields.some(f => f.id !== field.id && f.variableName === value);
      if (isDuplicate) {
        setVariableError('Variable name already exists');
      } else {
        setVariableError(null);
      }
    }
    
    updateField(field.id, { variableName: value });
  };
  
  // Auto-suggest variable name when label changes
  const handleLabelChange = (value: string) => {
    updateField(field.id, { label: value });
    
    // Show suggestion if variable name seems auto-generated or empty
    if (!field.variableName || field.variableName.startsWith('field') || showAutoSuggestion) {
      const suggested = generateVariableNameFromLabel(value);
      if (suggested && suggested !== field.variableName) {
        setShowAutoSuggestion(true);
      }
    }
  };
  return (
    <div 
      className="field-properties-panel bg-white border border-gray-200 rounded-md shadow-sm p-4"
      onClick={(e) => {
        e.stopPropagation();
        setIsEditingProperties(true);
      }}
    >
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-800">Field Properties</h3>
        <button 
          onClick={() => {
            console.log('Closing field properties panel');
            setSelectedFieldId(null);
            setIsEditingProperties(false);
          }}
          className="text-gray-400 hover:text-gray-600 bg-transparent rounded p-1 hover:bg-gray-100"
        >
          <span className="material-icons">close</span>
        </button>
      </div>
      
      <div className="space-y-4">
        {/* Label */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Label
          </label>
          <FieldPropertyInput
            value={field.label} 
            onChange={handleLabelChange}
            onFocus={() => setIsEditingProperties(true)}
          />
        </div>
        
        {/* Variable name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Variable Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
                {`{{${formSafeName}.`}
              </span>
              <FieldPropertyInput
                value={field.variableName} 
                onChange={handleVariableNameChange}
                onFocus={() => setIsEditingProperties(true)}
                placeholder="variableName"
              />
              <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
                {`}}`}
              </span>
            </div>
            {variableError && (
              <p className="text-xs text-red-500 mt-1">{variableError}</p>
            )}
            {showAutoSuggestion && (
              <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-200">
                <p className="text-xs text-blue-700">
                  Suggested variable name based on label:
                </p>
                <button
                  onClick={() => {
                    const suggested = generateVariableNameFromLabel(field.label);
                    handleVariableNameChange(suggested);
                    setShowAutoSuggestion(false);
                  }}
                  className="mt-1 text-xs font-mono bg-white px-2 py-1 rounded border border-blue-300 hover:bg-blue-100"
                >
                  Use "{generateVariableNameFromLabel(field.label)}"
                </button>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            This is how you'll reference this field in prompts
          </p>
        </div>
        
        {/* Placeholder - for appropriate field types */}
        {['text', 'textarea', 'number', 'select'].includes(field.type) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Placeholder
            </label>
            <FieldPropertyInput
              value={field.placeholder || ''} 
              onChange={(value) => updateField(field.id, { placeholder: value })}
              onFocus={() => setIsEditingProperties(true)}
            />
          </div>
        )}
        
        {/* Help text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Help Text
          </label>
          <textarea
            value={field.helpText || ''} 
            onChange={(e) => updateField(field.id, { helpText: e.target.value })}
            onFocus={() => setIsEditingProperties(true)}
            className="w-full px-3 py-2 text-sm text-gray-800 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
            rows={3}
            placeholder="Enter helpful instructions for users (supports multiple lines)"
          />
          <p className="text-xs text-gray-500 mt-1">
            Press Enter to create line breaks for better readability
          </p>
        </div>
        
        {/* Required checkbox */}
        <div className="flex items-center">
          <input 
            type="checkbox" 
            id="required" 
            checked={field.required} 
            onChange={(e) => updateField(field.id, { required: e.target.checked })}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <label htmlFor="required" className="ml-2 text-sm text-gray-700">
            Required field
          </label>
        </div>
        
        {/* Options for select, radio, checkbox */}
        {['select', 'radio'].includes(field.type) && field.options && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options
            </label>
            {field.options.map((option, index) => (
              <div key={index} className="flex items-center mb-2">
                <FieldPropertyInput
                  value={option.label} 
                  onChange={(value) => {
                    const newOptions = [...field.options!];
                    newOptions[index] = { 
                      ...newOptions[index], 
                      label: value,
                      value: value.toLowerCase().replace(/\s+/g, '_')
                    };
                    updateField(field.id, { options: newOptions });
                  }}
                  onFocus={() => setIsEditingProperties(true)}
                />
                <button 
                  onClick={() => {
                    if (field.options!.length <= 1) return;
                    const newOptions = [...field.options!];
                    newOptions.splice(index, 1);
                    updateField(field.id, { options: newOptions });
                  }}
                  className="ml-2 text-gray-400 hover:text-red-500 bg-gray-100 p-1 rounded hover:bg-red-50"
                  disabled={field.options!.length <= 1}
                >
                  <span className="material-icons">delete</span>
                </button>
              </div>
            ))}
            <button 
              onClick={() => {
                const newOptions = [...field.options!];
                const newOption = { 
                  label: `Option ${newOptions.length + 1}`, 
                  value: `option_${newOptions.length + 1}` 
                };
                updateField(field.id, { options: [...newOptions, newOption] });
              }}
              className="mt-2 px-3 py-1 text-sm text-blue-600 bg-white border border-blue-200 hover:bg-blue-50 hover:border-blue-300 rounded-md transition-colors"
            >
              + Add Option
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

// Helper functions for smart variable naming
const generateVariableNameFromLabel = (label: string): string => {
  // Remove special characters and convert to camelCase
  const words = label
    .toLowerCase()
    .replace(/[^\w\s]/g, '') // Remove special characters
    .trim()
    .split(/\s+/); // Split by whitespace
  
  if (words.length === 0) return 'field';
  
  // Handle common question patterns
  const questionStarters = ['what', 'how', 'when', 'where', 'why', 'which', 'who', 'is', 'are', 'do', 'does', 'can', 'will', 'would'];
  const filteredWords = words.filter(word => !questionStarters.includes(word));
  
  if (filteredWords.length === 0) {
    // If only question words remain, use the last significant word
    return words[words.length - 1];
  }
  
  // Convert to camelCase
  return filteredWords
    .map((word, index) => {
      if (index === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join('')
    .slice(0, 30); // Limit length to 30 chars
};

const validateVariableName = (name: string): { isValid: boolean; error?: string } => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: 'Variable name is required' };
  }
  
  if (!/^[a-zA-Z]/.test(name)) {
    return { isValid: false, error: 'Must start with a letter' };
  }
  
  if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) {
    return { isValid: false, error: 'Only letters, numbers, and underscores allowed' };
  }
  
  if (name.length > 30) {
    return { isValid: false, error: 'Maximum 30 characters' };
  }
  
  return { isValid: true };
};

// Update the interface for user form list items
interface UserFormListItem {
  id: string;
  name: string;
  description?: string;
  createdAt?: string;
  fields?: FormField[]; // Add fields to the interface
}

const SimpleFormDesigner: React.FC<SimpleFormDesignerProps> = ({ onFormSave = undefined }) => {
  const { user } = useAuth();
  const [fields, setFields] = useState<FormField[]>([]);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingProperties, setIsEditingProperties] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [userForms, setUserForms] = useState<UserFormListItem[]>([]);

  // Load user's forms on component mount
  useEffect(() => {
    const loadUserForms = async () => {
      if (!user?.id) return;
      
      try {
        const forms = await getUserForms(user.id);
        setUserForms(forms.map(form => ({ 
          id: form.id, 
          name: form.name, 
          description: form.description,
          createdAt: form.createdAt,
          fields: form.fields // Include fields in the state
        })));
      } catch (error) {
        console.error('Error loading user forms:', error);
      }
    };

    loadUserForms();
  }, [user?.id]);

  // Handle clicks outside to deselect field
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.field-preview-item') && 
          !target.closest('.field-properties-panel') && 
          !isEditingProperties) {
        console.log('Clicking outside - deselecting field');
        setSelectedFieldId(null);
        setIsEditingProperties(false); // Reset editing state
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditingProperties]);

  // Helper to get the selected field
  const getSelectedField = () => {
    return fields.find(field => field.id === selectedFieldId) || null;
  };
  
  // Add a new field
  const addField = (type: FieldType) => {
    const defaultLabel = getDefaultLabelForType(type);
    const baseVariableName = generateVariableNameFromLabel(defaultLabel);
    
    // Ensure unique variable name
    let variableName = baseVariableName;
    let counter = 1;
    while (fields.some(f => f.variableName === variableName)) {
      variableName = `${baseVariableName}${counter}`;
      counter++;
    }
    
    const newField: FormField = {
      id: generateId(),
      type,
      label: defaultLabel,
      required: false,
      variableName,
    };
    
    // Add default options for select, radio, checkbox
    if (type === 'select' || type === 'radio') {
      newField.options = [
        { label: 'Option 1', value: 'option_1' },
        { label: 'Option 2', value: 'option_2' }
      ];
    }
    
    const newFields = [...fields, newField];
    setFields(newFields);
    setSelectedFieldId(newField.id);
  };
  
  // Update a field
  const updateField = useCallback((id: string, updates: Partial<FormField>) => {
    setFields(currentFields => currentFields.map(field => 
      field.id === id ? { ...field, ...updates } : field
    ));
  }, []);
  
  // Remove a field
  const removeField = (id: string) => {
    setFields(fields.filter(field => field.id !== id));
    if (selectedFieldId === id) {
      setSelectedFieldId(null);
      setIsEditingProperties(false); // Reset editing state when removing selected field
    }
  };
  
  // Move a field up or down
  const moveField = (id: string, direction: 'up' | 'down') => {
    const index = fields.findIndex(field => field.id === id);
    if (index === -1) return;
    
    const newFields = [...fields];
    
    if (direction === 'up' && index > 0) {
      [newFields[index], newFields[index - 1]] = [newFields[index - 1], newFields[index]];
    } else if (direction === 'down' && index < fields.length - 1) {
      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
    }
    
    setFields(newFields);
  };
  
  // Save the current form
  const handleSaveForm = async () => {
    if (!user?.id) return;
    
    // Validate all variable names
    const invalidFields = fields.filter(field => {
      const validation = validateVariableName(field.variableName);
      return !validation.isValid;
    });
    
    if (invalidFields.length > 0) {
      setSaveMessage({ 
        text: `Please fix variable names for: ${invalidFields.map(f => f.label).join(', ')}`, 
        type: 'error' 
      });
      return;
    }

    // Check for duplicate variable names
    const variableNames = fields.map(f => f.variableName);
    const duplicates = variableNames.filter((name, index) => variableNames.indexOf(name) !== index);
    
    if (duplicates.length > 0) {
      setSaveMessage({ 
        text: `Duplicate variable names found: ${[...new Set(duplicates)].join(', ')}`, 
        type: 'error' 
      });
      return;
    }

    if (!formName.trim()) {
      setSaveMessage({ text: 'Form name is required', type: 'error' });
      return;
    }

    if (fields.length === 0) {
      setSaveMessage({ text: 'At least one field is required', type: 'error' });
      return;
    }

    setIsSaving(true);
    
    try {
      const formInput: Form = {
        id: formId || undefined,
        name: formName.trim(),
        description: formDescription.trim(),
        fields
      };

      const savedForm = await saveForm(formInput, user.id);
      
      // Update state with saved form
      setFormId(savedForm.id);
      
      // Update userForms list
      setUserForms(prev => {
        const existingIndex = prev.findIndex(f => f.id === savedForm.id);
        const newFormItem = { 
          id: savedForm.id, 
          name: savedForm.name, 
          description: savedForm.description,
          createdAt: savedForm.createdAt,
          fields: savedForm.fields // Include fields in the state
        };
        
        if (existingIndex >= 0) {
          // Update existing form
          const updated = [...prev];
          updated[existingIndex] = newFormItem;
          return updated;
        } else {
          // Add new form
          return [newFormItem, ...prev];
        }
      });
      
      setSaveMessage({ text: 'Form saved successfully!', type: 'success' });
      
      // Call the callback if provided
      if (onFormSave) {
        onFormSave(savedForm.id);
      }
      
      // Clear message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000);
      
    } catch (error: any) {
      console.error('Error saving form:', error);
      setSaveMessage({ text: error.message || 'Failed to save form', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Load a form
  const handleLoadForm = async (formId: string) => {
    try {
      const formData = await getFormById(formId);
      if (formData) {
        setFormId(formId);
        setFormName(formData.name);
        setFormDescription(formData.description || '');
        setFields(formData.fields);
        setSelectedFieldId(null);
        setIsEditingProperties(false); // Reset editing state
      }
    } catch (error) {
      console.error('Error loading form:', error);
      alert('Failed to load form');
    }
  };
  
  // Create a new form
  const handleNewForm = () => {
    setFormId(null);
    setFormName('');
    setFormDescription('');
    setFields([]);
    setSelectedFieldId(null);
    setIsEditingProperties(false); // Reset editing state
  };
  
  // Helper to generate default labels
  const getDefaultLabelForType = (type: FieldType): string => {
    switch (type) {
      case 'text': return 'Text Field';
      case 'textarea': return 'Text Area';
      case 'number': return 'Number Field';
      case 'select': return 'Dropdown';
      case 'checkbox': return 'Checkbox';
      case 'radio': return 'Radio Group';
      case 'date': return 'Date Field';
      case 'document': return 'Document Upload';
      case 'paragraph': return 'Paragraph Text';
      case 'header': return 'Section Header';
      default: return 'New Field';
    }
  };
  
  // Render field icons
  const getFieldIcon = (type: FieldType): JSX.Element => {
    // Simple placeholder icons
    const iconClass = "material-icons text-blue-600";
    
    switch (type) {
      case 'text':
        return <span className={iconClass}>text_fields</span>;
      case 'textarea':
        return <span className={iconClass}>notes</span>;
      case 'number':
        return <span className={iconClass}>pin</span>;
      case 'select':
        return <span className={iconClass}>arrow_drop_down</span>;
      case 'checkbox':
        return <span className={iconClass}>check_box</span>;
      case 'radio':
        return <span className={iconClass}>radio_button_checked</span>;
      case 'date':
        return <span className={iconClass}>calendar_today</span>;
      case 'document':
        return <span className={iconClass}>upload_file</span>;
      case 'paragraph':
        return <span className={iconClass}>text_snippet</span>;
      case 'header':
        return <span className={iconClass}>title</span>;
      default:
        return <span className={iconClass}>add_box</span>;
    }
  };
  
  // Render field content in preview
  const renderFieldPreview = (field: FormField): JSX.Element => {
    switch (field.type) {
      case 'text':
        return (
          <input 
            type="text" 
            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 bg-gray-50" 
            placeholder={field.placeholder || ''} 
            disabled 
          />
        );
      case 'textarea':
        return (
          <textarea 
            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 bg-gray-50" 
            placeholder={field.placeholder || ''} 
            rows={3} 
            disabled 
          />
        );
      case 'number':
        return (
          <input 
            type="number" 
            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 bg-gray-50" 
            placeholder={field.placeholder || ''} 
            disabled 
          />
        );
      case 'select':
        return (
          <select 
            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 bg-gray-50" 
            disabled
          >
            <option value="">{field.placeholder || 'Select an option'}</option>
            {field.options?.map((option, i) => (
              <option key={i} value={option.value}>{option.label}</option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <div className="flex items-center">
            <input 
              type="checkbox" 
              className="h-4 w-4 text-blue-600 border-gray-300 rounded" 
              disabled 
            />
            <span className="ml-2 text-gray-500">Option text</span>
          </div>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option, i) => (
              <div key={i} className="flex items-center">
                <input 
                  type="radio" 
                  className="h-4 w-4 text-blue-600 border-gray-300" 
                  disabled 
                />
                <span className="ml-2 text-gray-500">{option.label}</span>
              </div>
            ))}
          </div>
        );
      case 'date':
        return (
          <input 
            type="date" 
            className="block w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 bg-gray-50" 
            disabled 
          />
        );
      case 'document':
        return (
          <div className="flex items-center justify-center w-full">
            <label className="w-full flex flex-col items-center px-4 py-6 bg-white text-blue rounded-md tracking-wide uppercase cursor-pointer hover:bg-blue-50 border border-dashed border-gray-300">
              <span className="material-icons text-blue-500">upload_file</span>
              <span className="mt-2 text-sm text-gray-500">Select a file to upload</span>
              <input type="file" className="hidden" disabled />
            </label>
          </div>
        );
      case 'paragraph':
        return (
          <p className="text-sm text-gray-500">{field.label || 'Paragraph text'}</p>
        );
      case 'header':
        return (
          <h3 className="text-lg font-medium text-gray-800">{field.label || 'Section Header'}</h3>
        );
      default:
        return <div>Unknown field type</div>;
    }
  };
  
  // Field library showing available field types
  const FieldLibrary = () => {
    const fieldTypes: { type: FieldType; label: string }[] = [
      { type: 'text', label: 'Text Field' },
      { type: 'textarea', label: 'Text Area' },
      { type: 'number', label: 'Number' },
      { type: 'select', label: 'Dropdown' },
      { type: 'radio', label: 'Multiple Choice' },
      { type: 'checkbox', label: 'Checkbox' },
      { type: 'date', label: 'Date' },
      { type: 'document', label: 'File Upload' },
      { type: 'header', label: 'Section Header' },
      { type: 'paragraph', label: 'Paragraph Text' },
    ];
    
    return (
      <div className="bg-white border border-gray-200 rounded-md shadow-sm overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700">Add Field</h3>
        </div>
        <div className="p-2">
          <div className="grid grid-cols-2 gap-2">
            {fieldTypes.map((fieldType) => (
              <button
                key={fieldType.type}
                onClick={() => addField(fieldType.type)}
                className="flex flex-col items-center p-3 border border-gray-200 rounded hover:bg-blue-50 transition-colors bg-white shadow-sm"
              >
                {getFieldIcon(fieldType.type)}
                <span className="mt-1 text-xs text-gray-700">{fieldType.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
  // Enhanced My Forms component
  const MyFormsSection = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<'name' | 'date'>('date');
    const [showOnlyRecent, setShowOnlyRecent] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [isExpanded, setIsExpanded] = useState(true);

    const handleDeleteForm = async (formToDelete: UserFormListItem) => {
      if (deleteConfirm !== formToDelete.id) {
        setDeleteConfirm(formToDelete.id);
        return;
      }

      try {
        console.log('Deleting form:', formToDelete.id);
        await deleteForm(formToDelete.id);
        
        // Remove from local state
        setUserForms(prev => prev.filter(f => f.id !== formToDelete.id));
        
        // If we deleted the currently loaded form, reset
        if (formId === formToDelete.id) {
          handleNewForm();
        }
        
        setDeleteConfirm(null);
        setSaveMessage({ text: 'Form deleted successfully', type: 'success' });
        setTimeout(() => setSaveMessage(null), 3000);
      } catch (error: any) {
        console.error('Error deleting form:', error);
        setSaveMessage({ text: 'Failed to delete form: ' + (error.message || 'Unknown error'), type: 'error' });
        setDeleteConfirm(null);
      }
    };

    const handleDuplicateForm = async (formToDuplicate: UserFormListItem) => {
      try {
        console.log('Duplicating form:', formToDuplicate.id);
        const duplicatedForm = await duplicateForm(formToDuplicate.id, user?.id || 'anonymous');
        
        // Add to local state
        setUserForms(prev => [duplicatedForm, ...prev]);
        
        // Load the duplicated form
        await handleLoadForm(duplicatedForm.id);
        
        setSaveMessage({ text: 'Form duplicated successfully', type: 'success' });
        setTimeout(() => setSaveMessage(null), 3000);
      } catch (error: any) {
        console.error('Error duplicating form:', error);
        setSaveMessage({ text: 'Failed to duplicate form: ' + (error.message || 'Unknown error'), type: 'error' });
      }
    };

    // Filter and sort forms
    const filteredForms = useMemo(() => {
      let filtered = userForms.filter(form => 
        form.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (form.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
      );

      if (showOnlyRecent) {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        filtered = filtered.filter(form => 
          form.createdAt && new Date(form.createdAt) >= oneWeekAgo
        );
      }

      // Sort forms
      filtered.sort((a, b) => {
        if (sortBy === 'name') {
          return a.name.localeCompare(b.name);
        } else {
          // Sort by date - most recent first
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB.getTime() - dateA.getTime();
        }
      });

      return filtered;
    }, [userForms, searchTerm, sortBy, showOnlyRecent]);

    if (!isExpanded) {
      return (
        <div className="bg-white border border-gray-200 rounded-md shadow-sm mb-6">
          <div className="p-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-800">My Forms ({userForms.length})</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleNewForm}
                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                >
                  New Form
                </button>
                <button
                  onClick={() => setIsExpanded(true)}
                  className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors bg-white border border-gray-200"
                  title="Expand forms list"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white border border-gray-200 rounded-md shadow-sm mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-800">My Forms ({userForms.length})</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleNewForm}
                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              >
                New Form
              </button>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors bg-white border border-gray-200"
                title="Collapse forms list"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          
          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search forms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 bg-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'date')}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="date" className="text-gray-900 bg-white">Sort by Date</option>
                <option value="name" className="text-gray-900 bg-white">Sort by Name</option>
              </select>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={showOnlyRecent}
                  onChange={(e) => setShowOnlyRecent(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Recent only
              </label>
            </div>
          </div>
        </div>
        
        <div className="max-h-64 overflow-y-auto">
          {filteredForms.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              {userForms.length === 0 ? (
                <>
                  <svg width="48" height="48" className="mx-auto mb-3 text-gray-300" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <p>No forms created yet</p>
                  <p className="text-sm mt-1">Click "New Form" to create your first form</p>
                </>
              ) : (
                <p>No forms match your search criteria</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredForms.map((form) => (
                <div 
                  key={form.id} 
                  className={`p-3 hover:bg-gray-50 transition-colors ${
                    formId === form.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div 
                      className="flex-1 cursor-pointer" 
                      onClick={() => handleLoadForm(form.id)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-800 text-sm">{form.name}</h4>
                        {formId === form.id && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            Current
                          </span>
                        )}
                      </div>
                      {form.description && (
                        <p className="text-xs text-gray-600 mb-1 line-clamp-1">{form.description}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>{new Date(form.createdAt || '').toLocaleDateString()}</span>
                        <span>•</span>
                        <span>{form.fields?.length || 0} fields</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1 ml-3">
                      {deleteConfirm === form.id ? (
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-red-600">Confirm?</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteForm(form);
                            }}
                            className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                          >
                            Yes
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm(null);
                            }}
                            className="px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateForm(form);
                            }}
                            className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors bg-white border border-gray-200"
                            title="Duplicate form"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteForm(form);
                            }}
                            className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors bg-white border border-gray-200"
                            title="Delete form"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-5xl mx-auto p-4">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Form Designer</h1>
            <p className="text-gray-600">Create forms for your AI sequence</p>
          </div>
          <div className="flex space-x-2">
            <button 
              onClick={handleSaveForm}
              disabled={isSaving}
              className={`px-4 py-2 ${isSaving ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-md flex items-center`}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <span className="material-icons mr-1">save</span>
                  Save Form
                </>
              )}
            </button>
          </div>
        </div>
        
        {saveMessage && (
          <div className={`mb-4 p-3 rounded-md ${saveMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {saveMessage.text}
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Form preview - larger when no field is selected */}
          <div className={`bg-white rounded-md shadow-sm overflow-hidden ${selectedFieldId ? 'col-span-8' : 'col-span-9'}`}>
            <div className="p-6 border-b border-gray-200">
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="w-full text-2xl font-bold border-0 focus:ring-0 focus:outline-none mb-2 text-gray-900"
                placeholder="Form Title"
              />
              <textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                className="w-full text-gray-600 border-0 focus:ring-0 focus:outline-none resize-none"
                placeholder="Form Description"
              />
            </div>
            
            <div className="p-6">
              {fields.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  <p>No fields added yet. Select a field type from the right panel to add one.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {fields.map((field) => (
                    <div 
                      key={field.id} 
                      className={`field-preview-item p-4 border rounded-md relative cursor-pointer ${
                        selectedFieldId === field.id ? 
                        'border-blue-500 ring-1 ring-blue-500 bg-blue-50' : 
                        'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={(e) => {
                        // Only handle clicks if they're not on control buttons
                        const target = e.target as HTMLElement;
                        const isButton = target.closest('button') !== null;
                        
                        if (!isButton) {
                          console.log(`Selecting field: ${field.id}, current isEditingProperties: ${isEditingProperties}`);
                          setSelectedFieldId(field.id);
                          setIsEditingProperties(false); // Reset editing state on new selection
                        }
                      }}
                    >
                      <div className="absolute right-2 top-2 flex space-x-1">
                        <button 
                          onClick={(e) => { e.stopPropagation(); moveField(field.id, 'up'); }}
                          className="bg-gray-100 text-blue-600 hover:bg-blue-50 p-1 rounded"
                          disabled={fields.indexOf(field) === 0}
                        >
                          <span className="material-icons text-sm">arrow_upward</span>
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); moveField(field.id, 'down'); }}
                          className="bg-gray-100 text-blue-600 hover:bg-blue-50 p-1 rounded"
                          disabled={fields.indexOf(field) === fields.length - 1}
                        >
                          <span className="material-icons text-sm">arrow_downward</span>
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
                          className="bg-gray-100 text-red-500 hover:bg-red-50 p-1 rounded"
                        >
                          <span className="material-icons text-sm">delete</span>
                        </button>
                      </div>
                      
                      <div className="mb-1 flex items-center">
                        <label className="block text-sm font-medium text-gray-700">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                      </div>
                      
                      {field.helpText && (
                        <div className="text-xs text-gray-500 mb-2 whitespace-pre-line">
                          {field.helpText}
                        </div>
                      )}
                      
                      {renderFieldPreview(field)}
                      
                      {/* Show variable name as a prominent badge */}
                      <div className="mt-2 inline-flex items-center px-2 py-1 rounded text-xs font-mono bg-blue-50 text-blue-700 border border-blue-200">
                        <span className="material-icons text-xs mr-1">code</span>
                        {`{{${formName.toLowerCase().replace(/[^\w]/g, '') || 'form'}.${field.variableName}}}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Sidebar - field library or field properties */}
          <div className={`${selectedFieldId ? 'col-span-4' : 'col-span-3'}`}>
            {selectedFieldId && getSelectedField() ? (
              <FieldProperties 
                field={getSelectedField()!}
                updateField={updateField}
                setSelectedFieldId={setSelectedFieldId}
                setIsEditingProperties={setIsEditingProperties}
                formName={formName}
                fields={fields}
              />
            ) : (
              <FieldLibrary />
            )}
          </div>
        </div>

        {/* Enhanced My Forms Section - moved below the main form designer */}
        <div className="mt-8">
          <MyFormsSection />
        </div>
      </div>
    </div>
  );
};

export default SimpleFormDesigner; 